// src/components/diagnosis/MultiSearchInput.tsx

import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

interface MultiSearchInputProps {
  selectedItems: string[];
  onChange: (items: string[]) => void;
  searchSuggestions: string[];
  placeholder?: string;
}

const MultiSearchInput: React.FC<MultiSearchInputProps> = ({
  selectedItems,
  onChange,
  searchSuggestions,
  placeholder = "Search...",
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredSuggestions = searchSuggestions.filter(
    (suggestion) =>
      !selectedItems.includes(suggestion) &&
      suggestion.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleAddItem = (item: string) => {
    onChange([...selectedItems, item]);
    setInputValue('');
    setShowSuggestions(false);
  };

  const handleRemoveItem = (itemToRemove: string) => {
    onChange(selectedItems.filter((item) => item !== itemToRemove));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setShowSuggestions(true);
  };

  // Effect to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative w-full p-4">
       <h3 className="text-base font-semibold text-gray-900 mb-2">Diagnosis Codes</h3>
      <div className="flex flex-wrap items-center gap-2 p-2 border-2 rounded-lg bg-gray-50">
        {selectedItems.map((item) => (
          <div key={item} className="flex items-center gap-1 bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full">
            <span>{item}</span>
            <button onClick={() => handleRemoveItem(item)} className="rounded-full hover:bg-blue-200">
              <X size={14} />
            </button>
          </div>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          placeholder={selectedItems.length > 0 ? '' : placeholder}
          className="flex-1 bg-transparent outline-none p-1 min-w-[150px]"
        />
      </div>
      {showSuggestions && filteredSuggestions.length > 0 && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredSuggestions.map((suggestion) => (
            <li
              key={suggestion}
              onClick={() => handleAddItem(suggestion)}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100"
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MultiSearchInput;